import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, Chip, TextField, InputAdornment, Pagination,
  IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  Grid, Divider, CircularProgress
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { fetchData, putData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";

const PURPLE = "#244C77";
const SECONDARY = "#4A7BA8";

const STATUS_CONFIG = {
  "Escalated to Principal": { color: "#d97706", bg: "#fffbeb" },
  "Principal Approved":     { color: "#16a34a", bg: "#f0fdf4" },
  "Rejected":               { color: "#dc2626", bg: "#fef2f2" },
  "Escalated to Finance":   { color: "#336599", bg: "#f5f3ff" },
};

const FALLBACK_WITHDRAWALS = [
  {
    id: "1", studentName: "Arjun Rajan", registrationNo: "CTS-2024-001",
    grade: "Nilai 4", section: "A", reason: "Relocating to Chennai for family reasons",
    submittedDate: "2025-01-10", status: "Escalated to Principal",
    parentName: "Rajan Murugan", parentEmail: "rajan@example.com", phone: "404-555-0101",
    submittedBy: "Academic Coordinator",
  },
  {
    id: "2", studentName: "Priya Sharma", registrationNo: "CTS-2024-002",
    grade: "Nilai 6", section: "B", reason: "Change of residence — moving to Atlanta area",
    submittedDate: "2025-01-14", status: "Escalated to Principal",
    parentName: "Sharma Venkat", parentEmail: "sharma@example.com", phone: "404-555-0102",
    submittedBy: "Academic Coordinator",
  },
  {
    id: "3", studentName: "Karthik Mani", registrationNo: "CTS-2024-003",
    grade: "Mazhalai", section: "A", reason: "Health issues requiring extended care",
    submittedDate: "2025-01-18", status: "Escalated to Principal",
    parentName: "Mani Kumar", parentEmail: "mani@example.com", phone: "404-555-0103",
    submittedBy: "Secretary",
  },
  {
    id: "4", studentName: "Divya Lakshmi", registrationNo: "CTS-2024-004",
    grade: "Nilai 3", section: "C", reason: "Transfer to another Tamil school",
    submittedDate: "2025-01-20", status: "Principal Approved",
    parentName: "Lakshmi Nair", parentEmail: "lakshmi@example.com", phone: "404-555-0104",
    submittedBy: "Academic Coordinator",
  },
];

const ROWS_PER_PAGE = 10;

const PrincipalWithdrawal = () => {
  const [rows, setRows]             = useState([]);
  const [loading, setLoading]       = useState(true);
  const [filterText, setFilterText] = useState("");
  const [showFilter, setShowFilter] = useState(false);
  const [page, setPage]             = useState(1);
  const [detailDialog, setDetailDialog] = useState({ open: false, row: null });
  const [actionDialog, setActionDialog] = useState({ open: false, row: null, action: "", remarks: "" });
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast]           = useState({ open: false, severity: "success", message: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchData("principal/withdrawal?status=escalated");
      const data = res?.data?.withdrawals || res?.data || res || [];
      setRows(Array.isArray(data) && data.length > 0 ? data : FALLBACK_WITHDRAWALS);
    } catch (_) {
      setRows(FALLBACK_WITHDRAWALS);
      setToast({ open: true, severity: "warning", message: "Showing demo data — API unavailable" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = rows.filter(r => {
    if (!filterText) return true;
    const s = filterText.toLowerCase();
    return (
      (r.studentName || "").toLowerCase().includes(s) ||
      (r.registrationNo || "").toLowerCase().includes(s) ||
      (r.grade || "").toLowerCase().includes(s) ||
      (r.reason || "").toLowerCase().includes(s)
    );
  });

  const pageCount = Math.ceil(filtered.length / ROWS_PER_PAGE);
  const pageRows  = filtered.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE);

  const handleAction = async () => {
    const { row, action, remarks } = actionDialog;
    if (!row) return;
    setSubmitting(true);

    let payload = {};
    if (action === "approve")  payload = { status: "Principal Approved", remarks };
    if (action === "reject")   payload = { status: "Rejected", remarks };
    if (action === "escalate") payload = { status: "Escalated to Finance" };

    try {
      await putData(`principal/withdrawal/${row.id}`, payload);
      const msg = action === "approve"
        ? "Withdrawal approved successfully!"
        : action === "reject"
          ? "Withdrawal rejected."
          : "Escalated to Finance team.";
      setToast({ open: true, severity: "success", message: msg });
      setActionDialog({ open: false, row: null, action: "", remarks: "" });
      setDetailDialog({ open: false, row: null });
      load();
    } catch (err) {
      setToast({ open: true, severity: "error", message: err?.response?.data?.message || "Action failed." });
    } finally {
      setSubmitting(false);
    }
  };

  const openAction = (row, action) => {
    setActionDialog({ open: true, row, action, remarks: "" });
  };

  const StatusChip = ({ status }) => {
    const cfg = STATUS_CONFIG[status] || { color: "#6B7280", bg: "#f3f4f6" };
    return (
      <Chip label={status || "—"} size="small"
        sx={{ backgroundColor: cfg.bg, color: cfg.color, fontWeight: 600, fontSize: "11px", borderRadius: "20px" }} />
    );
  };

  const DetailField = ({ label, value }) => (
    <Box sx={{ mb: 1.5 }}>
      <Typography sx={{ fontSize: "11px", color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.5px", mb: 0.3 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "14px", color: "#111827", fontWeight: 500 }}>
        {value || "—"}
      </Typography>
    </Box>
  );

  const actionBtnLabel = () => {
    if (actionDialog.action === "approve")  return "Approve Withdrawal";
    if (actionDialog.action === "reject")   return "Reject Withdrawal";
    if (actionDialog.action === "escalate") return "Escalate to Finance";
    return "Confirm";
  };

  const actionBtnColor = () => {
    if (actionDialog.action === "approve")  return "#16a34a";
    if (actionDialog.action === "reject")   return "#dc2626";
    if (actionDialog.action === "escalate") return "#336599";
    return PURPLE;
  };

  return (
    <Box sx={{ p: 3, fontFamily: "'Inter', 'Roboto', sans-serif" }}>
      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })}
        severity={toast.severity} message={toast.message} />

      <Typography variant="body2" color="text.secondary" mb={0.5}>Pages / Withdrawal Approvals</Typography>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2, flexWrap: "wrap", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <ExitToAppIcon sx={{ color: "#dc2626", fontSize: "28px" }} />
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: "22px", color: "#111827" }}>Withdrawal Approvals</Typography>
            <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>
              Requests escalated to Principal level
            </Typography>
          </Box>
          <Chip
            label={`${rows.filter(r => r.status === "Escalated to Principal").length} pending`}
            size="small"
            sx={{ backgroundColor: "#fffbeb", color: "#d97706", fontWeight: 600, fontSize: "12px" }}
          />
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            startIcon={<FilterListIcon />} size="small" onClick={() => setShowFilter(v => !v)}
            sx={{ border: "1px solid #D5D7DA", borderRadius: "8px", color: "#8F95B2", textTransform: "none", fontSize: "13px" }}
          >
            Filters
          </Button>
          <Button
            startIcon={<FileDownloadOutlinedIcon />} size="small"
            sx={{ border: "1px solid #D5D7DA", borderRadius: "8px", color: "#8F95B2", textTransform: "none", fontSize: "13px" }}
          >
            Export
          </Button>
        </Box>
      </Box>

      {showFilter && (
        <Box sx={{ mb: 2 }}>
          <TextField
            size="small" fullWidth
            placeholder="Search by student name, registration number, grade, reason..."
            value={filterText} onChange={e => { setFilterText(e.target.value); setPage(1); }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "#8F95B2", fontSize: "18px" }} /></InputAdornment> }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
          />
        </Box>
      )}

      <Paper elevation={0} sx={{ borderRadius: "16px", boxShadow: "0 2px 12px rgba(0,0,0,0.07)", overflow: "hidden" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress sx={{ color: PURPLE }} />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead sx={{ background: `linear-gradient(135deg, ${PURPLE} 0%, ${SECONDARY} 100%)` }}>
                  <TableRow>
                    {["#", "Student Name", "Reg No", "Grade", "Section", "Reason", "Submission Date", "Status", "Actions"].map(h => (
                      <TableCell key={h} sx={{ color: "#fff", fontWeight: 600, fontSize: "12px", whiteSpace: "nowrap" }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pageRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} sx={{ textAlign: "center", py: 6, color: "#8F95B2", fontSize: "14px" }}>
                        No escalated withdrawal requests found
                      </TableCell>
                    </TableRow>
                  ) : pageRows.map((r, idx) => (
                    <TableRow
                      key={r.id || idx}
                      onClick={() => setDetailDialog({ open: true, row: r })}
                      sx={{ "&:hover": { backgroundColor: "#f9fafb" }, cursor: "pointer" }}
                    >
                      <TableCell sx={{ fontSize: "13px", color: "#6B7280" }}>{(page - 1) * ROWS_PER_PAGE + idx + 1}</TableCell>
                      <TableCell sx={{ fontSize: "13px", fontWeight: 600, color: "#111827" }}>{r.studentName}</TableCell>
                      <TableCell sx={{ fontSize: "12px", color: "#6B7280" }}>{r.registrationNo}</TableCell>
                      <TableCell>
                        <Chip label={r.grade} size="small" sx={{ backgroundColor: "#EAF1F8", color: PURPLE, fontSize: "11px" }} />
                      </TableCell>
                      <TableCell sx={{ fontSize: "13px", color: "#374151" }}>{r.section || "—"}</TableCell>
                      <TableCell sx={{ fontSize: "12px", color: "#374151", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {r.reason}
                      </TableCell>
                      <TableCell sx={{ fontSize: "12px", color: "#6B7280" }}>
                        {r.submittedDate ? new Date(r.submittedDate).toLocaleDateString("en-GB") : "—"}
                      </TableCell>
                      <TableCell onClick={e => e.stopPropagation()}>
                        <StatusChip status={r.status} />
                      </TableCell>
                      <TableCell onClick={e => e.stopPropagation()}>
                        {r.status === "Escalated to Principal" && (
                          <Box sx={{ display: "flex", gap: 0.5 }}>
                            <Tooltip title="Approve">
                              <IconButton size="small" onClick={() => openAction(r, "approve")}
                                sx={{ color: "#16a34a", "&:hover": { backgroundColor: "#f0fdf4" } }}>
                                <CheckCircleOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reject">
                              <IconButton size="small" onClick={() => openAction(r, "reject")}
                                sx={{ color: "#dc2626", "&:hover": { backgroundColor: "#fef2f2" } }}>
                                <CancelOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Escalate to Finance">
                              <IconButton size="small" onClick={() => openAction(r, "escalate")}
                                sx={{ color: "#336599", "&:hover": { backgroundColor: "#f5f3ff" } }}>
                                <AccountBalanceOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                        {r.status !== "Escalated to Principal" && (
                          <Tooltip title="View Details">
                            <IconButton size="small" sx={{ color: PURPLE }}>
                              <InfoOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {pageCount > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <Pagination count={pageCount} page={page} onChange={(_, v) => setPage(v)} size="small"
                  sx={{ "& .MuiPaginationItem-root.Mui-selected": { backgroundColor: PURPLE, color: "#fff" } }} />
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* Detail Dialog */}
      <Dialog
        open={detailDialog.open}
        onClose={() => setDetailDialog({ open: false, row: null })}
        maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <Box sx={{ background: `linear-gradient(135deg, ${PURPLE} 0%, ${SECONDARY} 100%)`, px: 3, py: 2.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box>
              <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "17px" }}>Withdrawal Request Details</Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.75)", fontSize: "12px" }}>{detailDialog.row?.registrationNo}</Typography>
            </Box>
            <IconButton size="small" onClick={() => setDetailDialog({ open: false, row: null })} sx={{ color: "#fff" }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {detailDialog.row && (
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography sx={{ fontWeight: 700, fontSize: "14px", color: "#374151", mb: 1.5 }}>Student Information</Typography>
                <DetailField label="Student Name"    value={detailDialog.row.studentName} />
                <DetailField label="Registration No" value={detailDialog.row.registrationNo} />
                <DetailField label="Grade"           value={detailDialog.row.grade} />
                <DetailField label="Section"         value={detailDialog.row.section} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography sx={{ fontWeight: 700, fontSize: "14px", color: "#374151", mb: 1.5 }}>Parent / Contact</Typography>
                <DetailField label="Parent Name"  value={detailDialog.row.parentName} />
                <DetailField label="Email"        value={detailDialog.row.parentEmail} />
                <DetailField label="Phone"        value={detailDialog.row.phone} />
                <DetailField label="Submitted By" value={detailDialog.row.submittedBy} />
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 0.5 }} />
              </Grid>
              <Grid item xs={12} sm={8}>
                <Typography sx={{ fontWeight: 700, fontSize: "14px", color: "#374151", mb: 1.5 }}>Reason for Withdrawal</Typography>
                <Paper sx={{ p: 2, borderRadius: 2, backgroundColor: "#f9fafb", border: "1px solid #E5E7EB" }}>
                  <Typography sx={{ fontSize: "14px", color: "#374151", lineHeight: 1.7 }}>
                    {detailDialog.row.reason}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography sx={{ fontWeight: 700, fontSize: "14px", color: "#374151", mb: 1.5 }}>Status</Typography>
                <StatusChip status={detailDialog.row.status} />
                <Typography sx={{ fontSize: "12px", color: "#9CA3AF", mt: 1.5 }}>
                  Submitted on {detailDialog.row.submittedDate ? new Date(detailDialog.row.submittedDate).toLocaleDateString("en-GB") : "—"}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button onClick={() => setDetailDialog({ open: false, row: null })}
            sx={{ border: "1.5px solid #D1D5DB", borderRadius: "10px", textTransform: "none", color: "#374151", px: 2 }}>
            Close
          </Button>
          {detailDialog.row?.status === "Escalated to Principal" && (
            <>
              <Button
                startIcon={<CancelOutlinedIcon />}
                onClick={() => openAction(detailDialog.row, "reject")}
                sx={{ border: "1.5px solid #fca5a5", borderRadius: "10px", textTransform: "none", color: "#dc2626", px: 2 }}
              >
                Reject
              </Button>
              <Button
                startIcon={<AccountBalanceOutlinedIcon />}
                onClick={() => openAction(detailDialog.row, "escalate")}
                sx={{ border: "1.5px solid #A9C2DB", borderRadius: "10px", textTransform: "none", color: "#336599", px: 2 }}
              >
                Escalate to Finance
              </Button>
              <Button
                startIcon={<CheckCircleOutlineIcon />}
                variant="contained"
                onClick={() => openAction(detailDialog.row, "approve")}
                sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600, backgroundColor: "#16a34a", boxShadow: "none", px: 2, "&:hover": { backgroundColor: "#15803d" } }}
              >
                Approve
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Action Confirmation Dialog */}
      <Dialog
        open={actionDialog.open}
        onClose={() => setActionDialog({ open: false, row: null, action: "", remarks: "" })}
        maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: "16px", p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: "17px" }}>{actionBtnLabel()}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "14px", color: "#374151", mb: 1.5 }}>
            Student: <strong>{actionDialog.row?.studentName}</strong>
          </Typography>
          {actionDialog.action !== "escalate" && (
            <TextField
              fullWidth size="small" multiline rows={3}
              label="Remarks"
              placeholder={actionDialog.action === "approve" ? "Optional remarks for approval..." : "Please provide reason for rejection..."}
              value={actionDialog.remarks}
              onChange={e => setActionDialog({ ...actionDialog, remarks: e.target.value })}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
            />
          )}
          {actionDialog.action === "escalate" && (
            <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>
              This will forward the withdrawal request to the Finance/Treasurer team for further processing.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setActionDialog({ open: false, row: null, action: "", remarks: "" })} fullWidth
            sx={{ border: "1.5px solid #D1D5DB", borderRadius: "10px", textTransform: "none", color: "#374151" }}>
            Cancel
          </Button>
          <Button onClick={handleAction} fullWidth variant="contained" disabled={submitting}
            sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600, boxShadow: "none", backgroundColor: actionBtnColor() }}>
            {submitting ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : actionBtnLabel()}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PrincipalWithdrawal;
